package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.unit.dp
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarIconButton
import com.partejar.app.core.ui.components.PartejarSkeleton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarLayout
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.components.PartejarWindowSize
import com.partejar.app.core.ui.components.partejarWindowSize
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.PartejarStatusBarAppearance
import com.partejar.app.feature.gestantes.data.GestanteClinicalUpdate
import com.partejar.app.feature.gestantes.data.GestanteDeliveryInput
import com.partejar.app.feature.gestantes.data.GestanteListStage
import com.partejar.app.feature.gestantes.presentation.components.gestationalAgeDescription
import com.partejar.app.feature.gestantes.presentation.components.GestationalHeroAnchor
import kotlin.math.abs
import org.koin.compose.koinInject

@Composable
fun GestanteDetailScreen(
    patientId: Long,
    onBack: () -> Unit,
    onOpenComorbidities: () -> Unit,
    onOpenExams: () -> Unit,
    viewModel: GestanteDetailViewModel = koinInject()
) {
    val state by viewModel.state.collectAsState()
    val expandedLayout = partejarWindowSize == PartejarWindowSize.EXPANDED
    LaunchedEffect(patientId) { viewModel.load(patientId) }
    val scrollState = rememberScrollState()
    var identityAnchor by remember { mutableStateOf<GestationalHeroAnchor?>(null) }
    var progressRingAnchor by remember { mutableStateOf<GestationalHeroAnchor?>(null) }
    var progressContentBounds by remember { mutableStateOf<Rect?>(null) }
    var compactHeaderBottom by remember { mutableStateOf<Float?>(null) }
    var compactAgeBounds by remember { mutableStateOf<Rect?>(null) }

    val collapseProgress = remember {
        derivedStateOf {
            val identity = identityAnchor
            val headerBottom = compactHeaderBottom
            if (identity == null || headerBottom == null) {
                0f
            } else {
                val identityTopInViewport = identity.top - scrollState.value
                ((headerBottom - identityTopInViewport) / identity.height.coerceAtLeast(1f))
                    .coerceIn(0f, 1f)
            }
        }
    }
    val identityAlpha = remember { derivedStateOf { 1f - collapseProgress.value } }
    val hasGestationalAge = (state as? GestanteDetailUiState.Content)?.detail?.let {
        gestationalAgeDescription(it.gestationalWeeks, it.gestationalDays) != null
    } == true
    // The compact header fades over the dark artwork, independently of the theme preference.
    PartejarStatusBarAppearance(
        lightIcons = gestanteStatusBarLightIcons(
            hasArtwork = hasGestationalAge,
            expandedLayout = expandedLayout,
            collapseProgress = collapseProgress.value,
            background = MaterialTheme.colorScheme.background
        )
    )
    val ringHandoffProgress = remember(hasGestationalAge) {
        derivedStateOf {
            if (!hasGestationalAge) 0f else gestationalRingHandoffProgress(
                ringAnchor = progressRingAnchor,
                scrollOffset = scrollState.value,
                compactHeaderBottom = compactHeaderBottom
            )
        }
    }
    val onIdentityPositioned = remember(scrollState) {
        { anchor: GestationalHeroAnchor ->
            val contentAnchor = anchor.offsetBy(scrollState.value.toFloat())
            if (identityAnchor.hasMeaningfulDifferenceFrom(contentAnchor)) identityAnchor = contentAnchor
        }
    }
    val onProgressRingPositioned = remember(scrollState) {
        { anchor: GestationalHeroAnchor ->
            val contentAnchor = anchor.offsetBy(scrollState.value.toFloat())
            if (progressRingAnchor.hasMeaningfulDifferenceFrom(contentAnchor)) progressRingAnchor = contentAnchor
        }
    }
    val onProgressContentPositioned = remember(scrollState) {
        { bounds: Rect ->
            val scrollOffset = scrollState.value.toFloat()
            val contentBounds = Rect(
                left = bounds.left,
                top = bounds.top + scrollOffset,
                right = bounds.right,
                bottom = bounds.bottom + scrollOffset
            )
            if (progressContentBounds.hasMeaningfulDifferenceFrom(contentBounds)) {
                progressContentBounds = contentBounds
            }
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        when (val current = state) {
            GestanteDetailUiState.Loading -> GestanteDetailLoading(Modifier.fillMaxSize())
            is GestanteDetailUiState.Error -> DetailError(
                current.message,
                Modifier.fillMaxSize(),
                onRetry = { viewModel.load(patientId) }
            )
            is GestanteDetailUiState.Content -> GestanteDetailContent(
                state = current,
                modifier = Modifier.fillMaxSize(),
                scrollState = scrollState,
                onBack = onBack,
                identityAlpha = identityAlpha,
                ringHandoffProgress = ringHandoffProgress,
                onIdentityPositioned = onIdentityPositioned,
                onProgressRingPositioned = onProgressRingPositioned,
                onProgressContentPositioned = onProgressContentPositioned,
                onEditingChanged = viewModel::setEditing,
                onSave = { viewModel.save(patientId, it) },
                onOpenComorbidities = onOpenComorbidities,
                onOpenExams = onOpenExams,
                onRegisterDelivery = { viewModel.registerDelivery(patientId, it) }
            )
        }
        if (!expandedLayout) {
            (state as? GestanteDetailUiState.Content)?.let { current ->
                GestanteContextualHeader(
                    detail = current.detail,
                    collapseProgress = collapseProgress,
                    ringHandoffProgress = ringHandoffProgress,
                    onAgePositioned = { bounds ->
                        if (compactAgeBounds.hasMeaningfulDifferenceFrom(bounds)) compactAgeBounds = bounds
                    },
                    onPositioned = { coordinates ->
                        val bottom = coordinates.boundsInRoot().bottom
                        if (compactHeaderBottom == null || abs(compactHeaderBottom!! - bottom) > 0.5f) {
                            compactHeaderBottom = bottom
                        }
                    }
                )
            }
            (state as? GestanteDetailUiState.Content)?.detail?.let { detail ->
                val sourceBounds = gestationalHandoffSourceBounds(
                    contentBounds = progressContentBounds,
                    ringAnchor = progressRingAnchor,
                    compactHeaderBottom = compactHeaderBottom
                )
                GestationalAgeHandoffOverlay(
                    detail = detail,
                    progress = ringHandoffProgress.value,
                    sourceBounds = sourceBounds,
                    destinationBounds = compactAgeBounds
                )
            }
        }
        if (!expandedLayout || state !is GestanteDetailUiState.Content) {
            GestanteBackAction(
                onBack = onBack,
                onMedia = state is GestanteDetailUiState.Content,
                contextualHeaderAlpha = collapseProgress
            )
        }
    }
}

@Composable
internal fun GestanteDetailContent(
    state: GestanteDetailUiState.Content,
    modifier: Modifier,
    scrollState: ScrollState,
    onBack: () -> Unit,
    identityAlpha: State<Float>,
    ringHandoffProgress: State<Float>,
    onIdentityPositioned: (GestationalHeroAnchor) -> Unit,
    onProgressRingPositioned: (GestationalHeroAnchor) -> Unit,
    onProgressContentPositioned: (Rect) -> Unit,
    onEditingChanged: (Boolean) -> Unit,
    onSave: (GestanteClinicalUpdate) -> Unit,
    onOpenComorbidities: () -> Unit,
    onOpenExams: () -> Unit,
    onRegisterDelivery: (GestanteDeliveryInput) -> Unit
) {
    if (partejarWindowSize == PartejarWindowSize.EXPANDED) {
        GestanteDetailExpandedContent(
            state = state,
            modifier = modifier,
            scrollState = scrollState,
            onBack = onBack,
            onEditingChanged = onEditingChanged,
            onSave = onSave,
            onOpenComorbidities = onOpenComorbidities,
            onOpenExams = onOpenExams,
            onRegisterDelivery = onRegisterDelivery
        )
        return
    }

    val spacing = PartejarTheme.spacing
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .imePadding()
            .verticalScroll(scrollState)
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
    ) {
        GestanteIdentification(
            detail = state.detail,
            identityAlpha = identityAlpha,
            ringHandoffProgress = ringHandoffProgress,
            onIdentityPositioned = onIdentityPositioned,
            onProgressRingPositioned = onProgressRingPositioned,
            onProgressContentPositioned = onProgressContentPositioned
        )
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .offset(y = -spacing.space20),
            shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
            color = MaterialTheme.colorScheme.surfaceContainerLowest,
            contentColor = MaterialTheme.colorScheme.onSurface,
            border = BorderStroke(
                PartejarTheme.dimensions.strokeThin,
                MaterialTheme.colorScheme.tertiary.copy(alpha = 0.22f)
            )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(
                        start = spacing.space16,
                        end = spacing.space16,
                        top = spacing.space32,
                        bottom = spacing.space16 + spacing.space20
                    ),
                verticalArrangement = Arrangement.spacedBy(spacing.space24)
            ) {
                GestanteClinicalSections(
                    state = state,
                    onEditingChanged = onEditingChanged,
                    onSave = onSave,
                    onOpenComorbidities = onOpenComorbidities,
                    onOpenExams = onOpenExams,
                    onRegisterDelivery = onRegisterDelivery
                )
            }
        }
    }
}

@Composable
private fun GestanteDetailExpandedContent(
    state: GestanteDetailUiState.Content,
    modifier: Modifier,
    scrollState: ScrollState,
    onBack: () -> Unit,
    onEditingChanged: (Boolean) -> Unit,
    onSave: (GestanteClinicalUpdate) -> Unit,
    onOpenComorbidities: () -> Unit,
    onOpenExams: () -> Unit,
    onRegisterDelivery: (GestanteDeliveryInput) -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .imePadding()
            .verticalScroll(scrollState)
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    start = spacing.space32,
                    end = spacing.space32,
                    top = spacing.space16,
                    bottom = PartejarLayout.desktopDockClearance
                ),
            horizontalArrangement = Arrangement.spacedBy(spacing.space32),
            verticalAlignment = Alignment.Top
        ) {
            Box(
                modifier = Modifier
                    .weight(0.42f)
                    .clip(PartejarShapes.radius28)
            ) {
                GestanteIdentification(
                    detail = state.detail,
                    identityAlpha = null,
                    ringHandoffProgress = null,
                    onIdentityPositioned = {},
                    onProgressRingPositioned = {},
                    onProgressContentPositioned = {},
                    showStatusBadge = false
                )
            }
            Column(
                modifier = Modifier.weight(0.58f),
                verticalArrangement = Arrangement.spacedBy(spacing.space16)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    PartejarIconButton(
                        onClick = onBack,
                        modifier = Modifier.size(dimensions.minimumTouchTarget)
                    ) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                    PartejarStatusBadge(
                        text = if (state.detail.stage == GestanteListStage.ACTIVE) "Ativa" else "Encerrada",
                        tone = if (state.detail.stage == GestanteListStage.ACTIVE) {
                            PartejarStatusBadgeTone.SUCCESS
                        } else {
                            PartejarStatusBadgeTone.NEUTRAL
                        }
                    )
                }
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(spacing.space24)
                ) {
                    GestanteClinicalSections(
                        state = state,
                        wideLayout = true,
                        onEditingChanged = onEditingChanged,
                        onSave = onSave,
                        onOpenComorbidities = onOpenComorbidities,
                        onOpenExams = onOpenExams,
                        onRegisterDelivery = onRegisterDelivery
                    )
                }
            }
        }
    }
}

@Composable
private fun GestanteClinicalSections(
    state: GestanteDetailUiState.Content,
    wideLayout: Boolean = false,
    onEditingChanged: (Boolean) -> Unit,
    onSave: (GestanteClinicalUpdate) -> Unit,
    onOpenComorbidities: () -> Unit,
    onOpenExams: () -> Unit,
    onRegisterDelivery: (GestanteDeliveryInput) -> Unit
) {
    state.actionError?.let {
        PartejarAlert("Não foi possível concluir", PartejarAlertTone.ERROR) { Text(it) }
    }
    ClinicalContext(state.detail, wideLayout = wideLayout)
    val active = state.detail.stage == GestanteListStage.ACTIVE
    if (state.editing && active) {
        EditClinicalData(state.detail, state.saving, onSave, onCancel = { onEditingChanged(false) })
    } else if (active) {
        GestanteEditAction(
            saving = state.saving,
            onClick = { onEditingChanged(true) }
        )
    }
    if (active && wideLayout) {
        Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)) {
            ClinicalSummaryNavigationCard(
                title = "Comorbidades",
                summary = comorbidityClinicalSummary(state.detail.comorbidities),
                icon = PartejarIcons.Navigation.prontuario,
                onClick = onOpenComorbidities,
                modifier = Modifier.weight(1f)
            )
            ClinicalSummaryNavigationCard(
                title = "Exames",
                summary = examClinicalSummary(state.detail.exams),
                icon = PartejarIcons.Clinical.exams,
                onClick = onOpenExams,
                modifier = Modifier.weight(1f)
            )
        }
    } else if (active) {
        ClinicalSummaryNavigationCard(
            title = "Comorbidades",
            summary = comorbidityClinicalSummary(state.detail.comorbidities),
            icon = PartejarIcons.Navigation.prontuario,
            onClick = onOpenComorbidities
        )
        ClinicalSummaryNavigationCard(
            title = "Exames",
            summary = examClinicalSummary(state.detail.exams),
            icon = PartejarIcons.Clinical.exams,
            onClick = onOpenExams
        )
    }
    if (active || state.detail.delivery != null) {
        DeliverySection(state.detail, state.saving, onRegisterDelivery)
    }
}

@Composable
private fun GestanteDetailLoading(modifier: Modifier) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = modifier
            .background(MaterialTheme.colorScheme.background)
            .verticalScroll(rememberScrollState())
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
            .padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space24)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space16),
            verticalAlignment = Alignment.CenterVertically
        ) {
            PartejarSkeleton(
                modifier = Modifier.size(spacing.space64),
                shape = PartejarShapes.pill
            )
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space8)
            ) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.76f).height(spacing.space24))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.52f).height(spacing.space12))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.64f).height(spacing.space12))
            }
        }
        repeat(3) {
            PartejarCard(Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.48f).height(spacing.space20))
                    PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.74f).height(spacing.space12))
                }
            }
        }
    }
}

@Composable
private fun DetailError(message: String, modifier: Modifier, onRetry: () -> Unit) {
    Column(
        modifier = modifier.fillMaxSize().padding(PartejarTheme.spacing.space16),
        verticalArrangement = Arrangement.Center
    ) {
        PartejarAlert("Não foi possível carregar", PartejarAlertTone.ERROR) { Text(message) }
        PartejarButton("Tentar novamente", onRetry, modifier = Modifier.padding(top = PartejarTheme.spacing.space16))
    }
}
